/* CASCATA
 * Cem células, cada uma um por cento. Com margem positiva o favo É o preço:
 * as quatro fatias somam exatamente ele.
 *
 * Com margem NEGATIVA o custo passa do preço, e não existe repartição do
 * preço que caiba: a leitura passa a ser sobre o CUSTO TOTAL, dito na nota da
 * seção e na legenda do favo, e o déficit ganha faixa própria embaixo, em
 * campo de perda.
 *
 * NÃO "CONSERTE" ISTO DE VOLTA. A troca de denominador é deliberada e está
 * escrita na tela. O jeito antigo era pior de duas maneiras ao mesmo tempo:
 * tomava o módulo da margem negativa, somava ao preço para formar o
 * denominador e continuava anunciando no título que aquilo era "cada real do
 * ticket" — um denominador inflado com rótulo de preço. Aqui, ou o
 * denominador é o preço e o título está certo, ou o denominador é o custo e a
 * tela diz isso em duas frases antes do primeiro número.
 *
 * O que não se faz em nenhum dos dois casos é somar o déficit às fatias: ele
 * já está dentro delas, e contá-lo de novo dobraria o buraco. (O ideal seria
 * o favo desenhar as células que não cabem FORA da moldura, mantendo as cem
 * como o preço; isso é mudança no design system, em CascataPreco.) */

import { Link } from 'react-router-dom';

import { CascataPreco } from '@/componentes/cascata-preco';
import { Camada, Celula } from '@/componentes/celula';
import { fatiasDoPreco } from '@/componentes/memoria-de-calculo';
import { Valor } from '@/componentes/valor';
import type { Cenario, Parametros } from '@/dados';
import { formatarMoeda } from '@/design/formato';

import { Cabecalho } from './cabecalho';
import type { NavegarPara } from './salvamento';

export function Cascata({
  p,
  atual,
  semPreco,
  aoNavegar,
}: {
  p: Parametros;
  /** Exigido: sem cenário este capítulo não é renderizado (ver index.tsx). */
  atual: Cenario;
  semPreco: boolean;
  aoNavegar: NavegarPara;
}) {
  const { fatias, margemPositiva, custoTotal } = fatiasDoPreco(p, atual);

  return (
    <Celula como="section" assenta atraso={340}>
      <Cabecalho
        titulo="Para onde vai cada real do preço"
        nota={
          semPreco
            ? 'Informe o valor de venda para ver a distribuição.'
            : margemPositiva
              ? `Cem células, cada uma um por cento de ${formatarMoeda(p.ticket)}. Aponte uma linha para acender o grupo dela no favo.`
              : `O custo passou do preço. As cem células mostram o custo total de ${formatarMoeda(custoTotal)}, e o buraco está logo abaixo.`
        }
      />

      {semPreco ? (
        <p className="apoio-nota">
          Sem preço não há o que repartir.{' '}
          <Link
            className="text-acao font-medium underline-offset-2 hover:underline"
            to="/projeto"
            onClick={aoNavegar('/projeto')}
          >
            Informar em Dados do projeto
          </Link>
        </p>
      ) : (
        <>
          <CascataPreco
            fatias={fatias}
            legendaFavo={
              margemPositiva
                ? 'Da primeira célula à centésima. O que fica com a empresa são as células de acento no rodapé.'
                : 'Com o custo acima do preço, as cem células são o custo total, não o preço.'
            }
            rodape={
              margemPositiva
                ? `As quatro fatias somam exatamente ${formatarMoeda(p.ticket)}. A última não é sobra de cálculo: é a margem de contribuição, e é dela que sai a estrutura da empresa.`
                : undefined
            }
          />

          {margemPositiva ? null : (
            <Camada className="bg-telha mt-4">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <span className="text-tinta text-apoio font-semibold">
                  Déficit: o custo que o preço não paga
                </span>
                <Valor valor={-atual.mcUnit} formato="moeda" tamanho="sub" tom="negativo" />
              </div>
              <p className="apoio-nota mt-1.5">
                O preço de {formatarMoeda(p.ticket)} entra e o custo de {formatarMoeda(custoTotal)}{' '}
                sai. A diferença não é margem apertada: é dinheiro que falta antes mesmo de pagar a
                estrutura.
              </p>
            </Camada>
          )}
        </>
      )}
    </Celula>
  );
}
